import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';
import { toast } from 'react-hot-toast';
import { Search, Plus, Edit2, X, ChevronLeft, ChevronRight, Trash2 } from 'lucide-react';

const inputStyle = { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
const btnPrimary = { background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const btnGhost = { background: 'none', border: '1px solid #d1d5db', borderRadius: 6, padding: '7px 8px', fontSize: 12, cursor: 'pointer', color: '#374151' };
const btnDanger = { background: 'none', border: '1px solid #fca5a5', borderRadius: 6, padding: '7px 8px', fontSize: 12, cursor: 'pointer', color: '#dc2626' };

const STATUS_COLORS = {
  pending:   { bg: '#fef3c7', text: '#92400e' },
  shipped:   { bg: '#dbeafe', text: '#1d4ed8' },
  cancelled: { bg: '#fee2e2', text: '#dc2626' },
};

function Field({ label, children, span }) {
  return (
    <div style={{ marginBottom: 14, gridColumn: span ? '1/-1' : undefined }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>{label}</label>
      {children}
    </div>
  );
}

function Modal({ title, onClose, children, wide }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 10, width: '100%', maxWidth: wide ? 680 : 480, maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #e5e7eb', position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#111827' }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}><X size={18} /></button>
        </div>
        <div style={{ padding: 20 }}>{children}</div>
      </div>
    </div>
  );
}

// ─── Customer search ──────────────────────────────────────────────────────────

function CustomerFilterSearch({ value, label, onChange }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const wrapRef = useRef(null);
  useEffect(() => {
    const h = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const search = async term => {
    if (!term) return setResults([]);
    try {
      const r = await api.get('/customers/', { params: { search: term, per_page: 10 } });
      setResults(r.data.customers || []);
    } catch {}
  };
  const handleInput = e => {
    setQ(e.target.value);
    if (!e.target.value) { onChange(null, ''); setResults([]); return; }
    setOpen(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => search(e.target.value), 300);
  };
  const select = c => {
    onChange(c.id, c.name || '', true);
    setQ(c.name || '');
    setOpen(false);
    setResults([]);
  };
  const clear = () => { setQ(''); onChange(null, '', false); setResults([]); };
  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <Search size={13} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
        <input value={value ? label : q} onChange={handleInput} onFocus={() => { if (!value) setOpen(true); }}
          placeholder="Search customer..." style={{ ...inputStyle, paddingLeft: 28, paddingRight: 28 }} />
        {(value || q) && <span onMouseDown={clear} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: '#9ca3af', fontSize: 16, lineHeight: 1 }}>×</span>}
      </div>
      {open && results.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 300, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', maxHeight: 260, overflowY: 'auto', marginTop: 2 }}>
          {results.map(c => (
            <div key={c.id} onMouseDown={() => select(c)}
              style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', display: 'flex', gap: 10, alignItems: 'center' }}
              onMouseEnter={e => e.currentTarget.style.background = '#f0f9ff'}
              onMouseLeave={e => e.currentTarget.style.background = ''}>
              {c.photo_url
                ? <img src={c.photo_url} alt="" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: '1px solid #e5e7eb' }} />
                : <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg,#7c6af5,#c084fc)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: '#fff', flexShrink: 0 }}>{(c.name || '?')[0].toUpperCase()}</div>
              }
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, color: '#111827', fontWeight: 600 }}>{c.name}</div>
                <div style={{ display: 'flex', gap: 8, marginTop: 1 }}>
                  {c.phone && <span style={{ fontSize: 11, color: '#6b7280' }}>📞 {c.phone}</span>}
                  {c.location_type && c.location_type !== 'unknown' && <span style={{ fontSize: 11, color: '#2563eb' }}>{c.location_type === 'phnom_penh' ? '📍 PP' : '🗺 Province'}</span>}
                  {c.ranking && c.ranking !== 'normal' && <span style={{ fontSize: 11, fontWeight: 600, color: c.ranking === 'platinum' ? '#c026d3' : c.ranking === 'gold' ? '#ca8a04' : '#94a3b8' }}>{c.ranking === 'platinum' ? '💎' : c.ranking === 'gold' ? '🥇' : '🥈'} {c.ranking}</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CustomerSearch({ value, onChange }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    const h = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const search = useCallback(async term => {
    if (!term) return;
    try {
      const r = await api.get('/customers/', { params: { search: term, per_page: 10 } });
      setResults(r.data.customers || []);
    } catch { }
  }, []);

  const handleInput = e => {
    setQ(e.target.value);
    setOpen(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => search(e.target.value), 300);
  };

  const select = c => {
    onChange(c);
    setQ(`${c.name || c.name_en} ${c.phone ? `(${c.phone})` : ''}`);
    setOpen(false);
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input value={value ? `${value.name || value.name_en || ''} ${value.phone ? `(${value.phone})` : ''}` : q}
        onChange={handleInput} onFocus={() => { setQ(''); onChange(null); setOpen(true); }}
        placeholder="Search customer name or phone..."
        style={inputStyle} />
      {open && results.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', maxHeight: 220, overflowY: 'auto', marginTop: 2 }}>
          {results.map(c => (
            <div key={c.id} onMouseDown={() => select(c)}
              style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', display: 'flex', gap: 10, alignItems: 'center' }}
              onMouseEnter={e => e.currentTarget.style.background = '#f0f9ff'}
              onMouseLeave={e => e.currentTarget.style.background = ''}>
              {/* Customer photo or avatar */}
              {c.photo_url
                ? <img src={c.photo_url} alt="" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: '1px solid #e5e7eb' }} />
                : <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg,#7c6af5,#c084fc)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
                    {(c.name || c.name_en || '?')[0].toUpperCase()}
                  </div>
              }
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, color: '#111827', fontWeight: 600 }}>{c.name || c.name_en}</div>
                <div style={{ display: 'flex', gap: 8, marginTop: 1 }}>
                  {c.phone && <span style={{ fontSize: 11, color: '#6b7280' }}>📞 {c.phone}</span>}
                  {c.location_type && c.location_type !== 'unknown' && <span style={{ fontSize: 11, color: '#2563eb' }}>{c.location_type === 'phnom_penh' ? '📍 PP' : '🗺 Province'}</span>}
                  {c.ranking && c.ranking !== 'normal' && <span style={{ fontSize: 11, color: c.ranking === 'platinum' ? '#c026d3' : c.ranking === 'gold' ? '#ca8a04' : '#94a3b8', fontWeight: 600 }}>{c.ranking === 'platinum' ? '💎' : c.ranking === 'gold' ? '🥇' : '🥈'} {c.ranking}</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Product search ───────────────────────────────────────────────────────────
function ProductSearch({ value, onChange }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    const h = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const search = useCallback(async term => {
    if (!term) return;
    try {
      const r = await api.get('/products/', { params: { q: term, per_page: 20, is_active: 1 } });
      setResults(r.data.items || []);
    } catch { }
  }, []);

  const handleInput = e => {
    setQ(e.target.value);
    setOpen(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => search(e.target.value), 300);
  };

  const select = p => {
    onChange(p);
    setQ(p.product_code);
    setOpen(false);
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input value={value ? value.product_code : q}
        onChange={handleInput}
        onFocus={() => { setQ(value ? value.product_code : q); setOpen(true); }}
        onKeyDown={e => { if (e.key === 'Backspace' || e.key === 'Delete') { onChange(null); setQ(''); } }}
        placeholder="Search product code..."
        style={inputStyle} />
      {open && results.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', maxHeight: 220, overflowY: 'auto', marginTop: 2 }}>
          {results.map(p => (
            <div key={p.id} onMouseDown={() => { if ((p.quantity - (p.total_ordered||0)) > 0) select(p); }}
              style={{ padding: '8px 12px', cursor: (p.quantity - (p.total_ordered||0)) > 0 ? 'pointer' : 'not-allowed', borderBottom: '1px solid #f3f4f6', display: 'flex', gap: 8, alignItems: 'center', opacity: (p.quantity - (p.total_ordered||0)) > 0 ? 1 : 0.45 }}
              onMouseEnter={e => { if ((p.quantity - (p.total_ordered||0)) > 0) e.currentTarget.style.background = '#f0f9ff'; }}
              onMouseLeave={e => e.currentTarget.style.background = ''}>
              <span style={{ fontFamily: 'monospace', fontSize: 11, background: '#eff6ff', color: '#1d4ed8', padding: '2px 6px', borderRadius: 4 }}>{p.product_code}</span>
              <span style={{ fontSize: 12, color: '#16a34a', fontWeight: 600 }}>${parseFloat(p.selling_price || 0).toFixed(2)}</span>
              <span style={{ fontSize: 11, color: (p.quantity - (p.total_ordered||0)) <= 0 ? '#dc2626' : '#6b7280', fontWeight: (p.quantity - (p.total_ordered||0)) <= 0 ? 700 : 400 }}>
                {(p.quantity - (p.total_ordered||0))}/{p.quantity} left
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Add/Edit Order Modal ─────────────────────────────────────────────────────
function OrderModal({ order, prefill, onClose, onSaved }) {
  const isEdit = !!order;
  const [customer, setCustomer] = useState(order ? { id: order.customer_id, name: order.customer_name, phone: order.customer_phone } : prefill ? { id: prefill.customer_id, name: prefill.customer_name, phone: prefill.customer_phone } : null);
  const [product, setProduct] = useState(order ? { id: order.product_id, product_code: order.product_code, selling_price: order.product_price } : prefill ? { id: prefill.product_id, product_code: prefill.product_code, selling_price: prefill.product_price } : null);
  const src = order || prefill || {};
  const [form, setForm] = useState({
    quantity: src.quantity || 1,
    unit_price: src.unit_price || '',
    remarks: src.remarks || '',
    staff_note: src.staff_note || '',
    status: 'pending',
    cancel_reason: '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Auto-fill unit price from product
  useEffect(() => {
    if (product && !isEdit) {
      set('unit_price', parseFloat(product.selling_price || 0).toFixed(2));
    }
  }, [product]);

  const total = (parseFloat(form.unit_price) || 0) * (parseInt(form.quantity) || 0);

  const submit = async () => {
    if (!customer) return toast.error('Select a customer');
    setSaving(true);
    try {
      const payload = {
        customer_id: customer.id,
        product_id: product?.id || null,
        quantity: parseInt(form.quantity),
        unit_price: parseFloat(form.unit_price) || 0,
        remarks: form.remarks,
        staff_note: form.staff_note,
        status: form.status,
        cancel_reason: form.cancel_reason,
      };
      if (isEdit) {
        await api.put(`/orders/simple/${order.id}`, payload);
        toast.success('Order updated');
      } else {
        await api.post('/orders/simple', payload);
        toast.success('Order created');
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={isEdit ? `Edit — ${order.order_number}` : 'Add Order'} onClose={onClose} wide>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>

        <Field label="Customer *" span>
          <CustomerSearch value={customer} onChange={setCustomer} />
        </Field>

        <Field label="Product" span>
          <ProductSearch value={product} onChange={p => { setProduct(p); if (p) set('unit_price', parseFloat(p.selling_price || 0).toFixed(2)); }} />
        </Field>

        <Field label="Quantity">
          <input type="number" min="1" value={form.quantity} onChange={e => set('quantity', e.target.value)} style={inputStyle} />
        </Field>

        <Field label="Unit Price (USD)">
          <input type="number" step="0.01" value={form.unit_price} onChange={e => set('unit_price', e.target.value)} style={inputStyle} />
        </Field>

        {/* Total display */}
        <div style={{ gridColumn: '1/-1', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 13, color: '#374151' }}>Total</span>
          <span style={{ fontSize: 18, fontWeight: 700, color: '#16a34a' }}>${total.toFixed(2)}</span>
        </div>

        <Field label="Remarks" span>
          <textarea value={form.remarks} onChange={e => set('remarks', e.target.value)} style={{ ...inputStyle, minHeight: 70, resize: 'vertical' }} placeholder="Customer comment, phone, location, notes..." />
        </Field>

        <Field label="Staff Note" span>
          <textarea value={form.staff_note} onChange={e => set('staff_note', e.target.value)} style={{ ...inputStyle, minHeight: 50, resize: 'vertical' }} placeholder="Internal note..." />
        </Field>

        

        {(isEdit && form.status === 'cancelled') && (
          <Field label="Cancel Reason">
            <input value={form.cancel_reason} onChange={e => set('cancel_reason', e.target.value)} style={inputStyle} />
          </Field>
        )}

      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20, paddingTop: 16, borderTop: '1px solid #e5e7eb' }}>
        <button onClick={onClose} style={{ ...btnGhost, padding: '9px 18px' }}>Cancel</button>
        <button onClick={submit} disabled={saving} style={btnPrimary}>{saving ? 'Saving...' : isEdit ? 'Update' : 'Add Order'}</button>
      </div>
    </Modal>
  );
}

// ─── Delete Confirm Modal ─────────────────────────────────────────────────────
function DeleteConfirm({ order, onClose, onDeleted }) {
  const [confirm, setConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);

  const doDelete = async () => {
    if (confirm !== order.order_number) return toast.error('Type the order number to confirm');
    setDeleting(true);
    try {
      await api.delete(`/orders/${order.id}`);
      toast.success(`Order ${order.order_number} deleted`);
      onDeleted();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setDeleting(false); }
  };

  return (
    <Modal title="Delete Order" onClose={onClose}>
      <div style={{ textAlign: 'center', padding: '8px 0 16px' }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
        <div style={{ fontSize: 14, color: '#374151', marginBottom: 8 }}>You are about to delete:</div>
        <div style={{ fontFamily: 'monospace', fontSize: 16, fontWeight: 700, color: '#dc2626', marginBottom: 16 }}>{order.order_number}</div>
        <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>This action cannot be undone. Type the order number to confirm:</div>
        <input value={confirm} onChange={e => setConfirm(e.target.value)} style={{ ...inputStyle, textAlign: 'center', fontFamily: 'monospace', letterSpacing: 1 }} placeholder={order.order_number} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
        <button onClick={onClose} style={{ ...btnGhost, padding: '9px 18px' }}>Cancel</button>
        <button onClick={doDelete} disabled={deleting || confirm !== order.order_number}
          style={{ ...btnPrimary, background: confirm === order.order_number ? '#dc2626' : '#f3f4f6', color: confirm === order.order_number ? '#fff' : '#9ca3af' }}>
          {deleting ? 'Deleting...' : 'Delete'}
        </button>
      </div>
    </Modal>
  );
}

function copyName(name) {
  navigator.clipboard.writeText(name || '').then(() => toast.success('Copied: ' + name)).catch(() => {});
}

// ─── Quick Fill Modal ─────────────────────────────────────────────────────────
function cleanPhoneQF(raw) {
  if (!raw) return '';
  const khmer = '០១២៣៤៥៦៧៨៩';
  let s = raw.split('').map(c => { const i = khmer.indexOf(c); return i >= 0 ? String(i) : c; }).join('');
  return s.replace(/[^\d+]/g, '');
}

function QuickFillModal({ order, onClose, onSaved }) {
  const [deliveryMethods, setDeliveryMethods] = useState([]);
  const [editingPhone, setEditingPhone] = useState(false);
  const [phones, setPhones] = useState([]);
  const parsePhones = (p) => { try { return Array.isArray(p) ? p : (typeof p === 'string' ? JSON.parse(p) : []); } catch { return []; } };
  const [editingAddr, setEditingAddr] = useState(false);
  const [address, setAddress] = useState(order.customer_address || '');
  const [delivery, setDelivery] = useState(order.customer_preferred_delivery || '');

  useEffect(() => {
    api.get('/customers/delivery-methods').then(r => setDeliveryMethods(r.data.filter(m => m.is_active))).catch(() => {});
  }, []);

  const inpStyle = { background: '#fff', border: '1px solid #7c6af5', borderRadius: 6, padding: '4px 8px', fontSize: 12, outline: 'none', width: '100%' };
  const rowStyle = { marginBottom: 14, paddingBottom: 14, borderBottom: '1px solid #f3f4f6' };
  const labelStyle = { fontSize: 11, fontWeight: 600, color: '#9ca3af', marginBottom: 6, display: 'block' };
  const ab = (bg, color) => ({ background: bg, color, border: 'none', borderRadius: 4, padding: '2px 8px', fontSize: 11, cursor: 'pointer' });

  const startPhone = () => {
    const init = (() => { const p = parsePhones(order.customer_phones); return p.length ? p : order.customer_phone ? [order.customer_phone] : ['']; })();
    setPhones(init);
    setEditingPhone(true);
  };
  const savePhone = async () => {
    const cleaned = phones.map(cleanPhoneQF).filter(Boolean);
    try {
      await api.patch(`/customers/${order.customer_id}`, { phones: cleaned, phone: cleaned[0]||'', phone2: cleaned[1]||'' });
      toast.success('Phone saved');
      setEditingPhone(false);
      onSaved();
    } catch { toast.error('Failed'); }
  };
  const saveAddr = async () => {
    try {
      await api.patch(`/customers/${order.customer_id}`, { address });
      toast.success('Address saved');
      setEditingAddr(false);
      onSaved();
    } catch { toast.error('Failed'); }
  };
  const saveDel = async (val) => {
    try {
      await api.patch(`/customers/${order.customer_id}`, { preferred_delivery: val || null });
      toast.success(val ? 'Delivery set' : 'Delivery cleared');
      setDelivery(val);
      onSaved();
    } catch { toast.error('Failed'); }
  };

  return (
    <Modal title={`Quick Fill — ${order.customer_name}`} onClose={onClose}>
      <div style={rowStyle}>
        <span style={labelStyle}>📞 PHONE</span>
        {editingPhone ? (
          <div onClick={e => e.stopPropagation()}>
            {phones.map((p, i) => (
              <div key={i} style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
                <input value={p} onChange={e => { const n=[...phones]; n[i]=e.target.value; setPhones(n); }}
                  onKeyDown={e => { if (e.key==='Enter') savePhone(); if (e.key==='Escape') setEditingPhone(false); }}
                  style={inpStyle} autoFocus={i===0} placeholder={i===0?'Primary phone':`Phone ${i+1}`} />
                {phones.length > 1 && <button onClick={() => setPhones(phones.filter((_,j)=>j!==i))} style={ab('#fee2e2','#dc2626')}>×</button>}
              </div>
            ))}
            <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
              <button onClick={() => setPhones([...phones,''])} style={ab('#f0f9ff','#0369a1')}>+</button>
              <button onClick={savePhone} style={ab('#7c6af5','#fff')}>✓</button>
              <button onClick={() => setEditingPhone(false)} style={ab('#f3f4f6','#374151')}>✕</button>
            </div>
          </div>
        ) : (
          <div onClick={startPhone} style={{ cursor: 'text', minHeight: 22 }} title="Click to edit">
            {(() => { const p = parsePhones(order.customer_phones); return p.length ? p.map((ph,i) => <div key={i} style={{ fontSize: 12, color: '#374151' }}>{ph}</div>) : order.customer_phone ? <div style={{ fontSize: 12, color: '#374151' }}>{order.customer_phone}</div> : <span style={{ color: '#adb5bd', fontSize: 12 }}>+ Add phone</span>; })()}
          </div>
        )}
      </div>

      <div style={rowStyle}>
        <span style={labelStyle}>📍 ADDRESS</span>
        {editingAddr ? (
          <div>
            <input value={address} onChange={e => setAddress(e.target.value)} autoFocus
              onKeyDown={e => { if (e.key==='Enter') saveAddr(); if (e.key==='Escape') setEditingAddr(false); }}
              style={{ ...inpStyle, marginBottom: 4 }} placeholder="Address..." />
            <div style={{ display: 'flex', gap: 4 }}>
              <button onClick={saveAddr} style={ab('#7c6af5','#fff')}>✓ Save</button>
              <button onClick={() => setEditingAddr(false)} style={ab('#f3f4f6','#374151')}>✕</button>
            </div>
          </div>
        ) : (
          <div onClick={() => { setAddress(order.customer_address||''); setEditingAddr(true); }} style={{ cursor: 'text', minHeight: 22 }} title="Click to edit">
            {order.customer_address
              ? <div style={{ fontSize: 12, color: '#374151' }}>{order.customer_address}</div>
              : <span style={{ color: '#adb5bd', fontSize: 12 }}>+ Add address</span>}
          </div>
        )}
      </div>

      <div>
        <span style={labelStyle}>🚚 PREFERRED DELIVERY</span>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {deliveryMethods.map(m => (
            <button key={m.code} onClick={() => saveDel(delivery === m.code ? '' : m.code)}
              style={{ background: delivery===m.code ? '#0369a1' : '#e0f2fe', color: delivery===m.code ? '#fff' : '#0369a1', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
              {m.name}
            </button>
          ))}
          {delivery && <button onClick={() => saveDel('')} style={ab('#fee2e2','#dc2626')}>clear</button>}
        </div>
      </div>
    </Modal>
  );
}

// ─── Main Orders Page ─────────────────────────────────────────────────────────
export default function Orders() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const [filterStatus, setFilterStatus] = useState('');
  const [filterQ, setFilterQ] = useState('');
  const [filterCustomerId, setFilterCustomerId] = useState(null);
  const [filterCustomerName, setFilterCustomerName] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  const [showAdd, setShowAdd] = useState(false);
  const [editOrder, setEditOrder] = useState(null);
  const [deleteOrder, setDeleteOrder] = useState(null);
  const [duplicating, setDuplicating] = useState(null);
  const [quickFill, setQuickFill] = useState(null);

  useEffect(() => { loadOrders(); }, [page, filterStatus]);

  const loadOrders = async (forcePage, forceQ, forceStatus, forceCustId) => {
    setLoading(true);
    try {
      const params = { page, per_page: 50 };
      const status = forceStatus !== undefined ? forceStatus : filterStatus;
      const q = forceQ !== undefined ? forceQ : filterQ;
      const custId = forceCustId !== undefined ? forceCustId : filterCustomerId;
      if (status) params.status = status;
      if (custId) params.customer_id = custId;
      if (q) params.q = q;

      if (filterDateFrom) params.date_from = filterDateFrom;
      if (filterDateTo) params.date_to = filterDateTo;
      const r = await api.get('/orders/simple', { params });
      setItems(r.data.items || r.data.orders || []);
      setTotal(r.data.total || r.data.count || 0);
      setPages(Math.max(r.data.pages || 1, 1));
    } catch { toast.error('Failed to load orders'); }
    finally { setLoading(false); }
  };

  const duplicate = order => {
    setShowAdd({ prefill: order });
  };

  return (
    <div style={{ padding: 24, minHeight: '100vh', background: '#f8fafc' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#111827' }}>Orders</h1>
          <p style={{ margin: '2px 0 0', fontSize: 13, color: '#6b7280' }}>{total.toLocaleString()} orders</p>
        </div>
        <button onClick={() => setShowAdd(true)} style={{ ...btnPrimary, display: 'flex', alignItems: 'center', gap: 6 }}><Plus size={14} /> Add Order</button>
      </div>

      {/* Filters */}
      <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e5e7eb', padding: '14px 16px', marginBottom: 16, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
        <div style={{ flex: '1 1 160px' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>ORDER / PRODUCT</div>
          <div style={{ position: 'relative' }}>
            <Search size={13} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
            <input value={filterQ} onChange={e => setFilterQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && (setPage(1), loadOrders(1, filterQ, filterStatus, filterCustomerId))}
              placeholder="Order no, product code..." style={{ ...inputStyle, paddingLeft: 28 }} />
          </div>
        </div>
        <div style={{ flex: '1 1 160px' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>CUSTOMER</div>
          <CustomerFilterSearch value={filterCustomerId} label={filterCustomerName} onChange={(id, name, autoSearch) => { setFilterCustomerId(id); setFilterCustomerName(name || ''); if (autoSearch) { setPage(1); setTimeout(() => loadOrders(1, filterQ, filterStatus, id), 0); } }} />
        </div>
        <div style={{ flex: '0 0 130px' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>STATUS</div>
          <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }} style={inputStyle}>
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="shipped">Shipped</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <div style={{ flex: '0 0 130px' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>FROM</div>
          <input type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} style={inputStyle} />
        </div>
        <div style={{ flex: '0 0 130px' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>TO</div>
          <input type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} style={inputStyle} />
        </div>
        <button onClick={() => { setPage(1); loadOrders(1, filterQ, filterStatus, filterCustomerId); }} style={btnPrimary}>Search</button>
      </div>

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
              {['Order No', 'Date Created', 'Customer', 'Product', 'Qty', 'Unit Price', 'Total', 'Remarks', 'Status', 'Action'].map(h => (
                <th key={h} style={{ padding: '10px 12px', textAlign: ['Qty','Unit Price','Total'].includes(h) ? 'center' : 'left', fontWeight: 600, color: '#374151', fontSize: 12, whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={10} style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading...</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={10} style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>No orders yet</td></tr>
            )}
            {items.map(item => {
              const sc = STATUS_COLORS[item.status] || { bg: '#f3f4f6', text: '#374151' };
              const canDelete = item.status === 'pending';
              const canEdit = !['shipped', 'delivered', 'packed'].includes(item.status);
              return (
                <tr key={item.id} style={{ borderBottom: '1px solid #f3f4f6' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}>
                  {/* Order No */}
                  <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                    <div style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: '#1d4ed8' }}>{item.order_number}</div>
                    {item.created_by_name && <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>by {item.created_by_name}</div>}
                  </td>
                  {/* Date & Staff */}
                  <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                    <div style={{ fontSize: 12, color: '#374151' }}>{item.created_at ? new Date(item.created_at).toLocaleDateString('en-GB') : '—'}</div>
                    <div style={{ fontSize: 11, color: '#9ca3af' }}>{item.created_at ? new Date(item.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }) : ''}</div>

                  </td>
                  {/* Customer */}
                  <td style={{ padding: '8px 12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {(() => {
                        const bc = item.customer_is_blacklisted ? '#f87171' : item.customer_ranking === 'platinum' ? '#1f2937' : item.customer_ranking === 'gold' ? '#fbbf24' : item.customer_ranking === 'silver' ? '#cbd5e1' : item.customer_is_new ? '#4ade80' : '#93c5fd';
                        return item.customer_photo
                          ? <img src={item.customer_photo} alt="" onClick={e => { e.stopPropagation(); copyName(item.customer_name); }} style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', border: `2.5px solid ${bc}`, flexShrink: 0, cursor: 'pointer' }} title="Click to copy name" />
                          : <div onClick={e => { e.stopPropagation(); copyName(item.customer_name); }} style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg,#7c6af5,#c084fc)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: '#fff', flexShrink: 0, border: `2.5px solid ${bc}`, cursor: 'pointer' }} title="Click to copy name">{(item.customer_name || '?')[0].toUpperCase()}</div>;
                      })()
                      }
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                          <div onClick={() => setQuickFill(item)} style={{ fontSize: 13, color: '#111827', fontWeight: 500, cursor: 'pointer' }}>{item.customer_name || '—'}</div>
                          {item.customer_is_blacklisted ? <span style={{ fontSize: 10 }}>⛔</span> : item.customer_ranking === 'platinum' ? <span style={{ fontSize: 10 }}>💎</span> : item.customer_ranking === 'gold' ? <span style={{ fontSize: 10 }}>🥇</span> : item.customer_ranking === 'silver' ? <span style={{ fontSize: 10 }}>🥈</span> : item.customer_is_new ? <span style={{ fontSize: 10 }}>🟢</span> : null}
                        </div>
                        <div style={{ display: 'flex', gap: 4, marginTop: 2 }}>
                          <span title='Phone' style={{ fontSize: 11, opacity: item.customer_phone ? 1 : 0.2 }}>📞</span>
                          <span title='Location' style={{ fontSize: 11, opacity: item.customer_address ? 1 : 0.2 }}>📍</span>
                          <span title='Delivery' style={{ fontSize: 11, opacity: item.customer_preferred_delivery ? 1 : 0.2 }}>🚚</span>
                        </div>
                      </div>
                    </div>
                  </td>
                  {/* Product */}
                  <td style={{ padding: '8px 12px', maxWidth: 200 }}>
                    {item.product_code
                      ? <span style={{ fontFamily: 'monospace', fontSize: 10, color: '#1d4ed8', background: '#eff6ff', padding: '2px 6px', borderRadius: 4, wordBreak: 'break-all' }}>{item.product_code}</span>
                      : <span style={{ color: '#9ca3af', fontSize: 12 }}>—</span>}
                  </td>
                  {/* Qty */}
                  <td style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, color: '#111827' }}>{item.quantity}</td>
                  {/* Unit Price */}
                  <td style={{ padding: '8px 12px', textAlign: 'center', color: '#374151' }}>
                    {`$${parseFloat(item.unit_price || 0).toFixed(2)}`}
                  </td>
                  {/* Total */}
                  <td style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 700, color: '#16a34a', whiteSpace: 'nowrap' }}>
                    {`$${parseFloat(item.total || 0).toFixed(2)}`}
                  </td>
                  {/* Remarks */}
                  <td style={{ padding: '8px 12px', fontSize: 12, color: '#374151' }}>
                    {item.remarks ? <span>{item.remarks}</span> : <span style={{ color: '#9ca3af' }}>—</span>}
                  </td>
                  {/* Status */}
                  <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                    <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 10, background: sc.bg, color: sc.text, whiteSpace: 'nowrap' }}>
                      {item.status?.charAt(0).toUpperCase() + item.status?.slice(1)}
                    </span>
                    {item.cancel_reason && <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>{item.cancel_reason.slice(0, 20)}</div>}
                  </td>
                  {/* Action */}
                  <td style={{ padding: '8px 12px' }}>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button onClick={() => setEditOrder(item)} style={{ ...btnGhost, opacity: canEdit ? 1 : 0.4 }} title="Edit" disabled={!canEdit}><Edit2 size={13} /></button>
                      <button onClick={() => duplicate(item)} style={btnGhost} title="Duplicate" disabled={duplicating === item.id}>⧉</button>
                      <button onClick={() => canDelete && setDeleteOrder(item)} style={{ ...btnDanger, opacity: canDelete ? 1 : 0.3 }} title={canDelete ? 'Delete' : 'Only pending orders can be deleted'} disabled={!canDelete}><Trash2 size={13} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {pages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, padding: 14, borderTop: '1px solid #e5e7eb' }}>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={btnGhost}><ChevronLeft size={14} /></button>
            <span style={{ fontSize: 13, color: '#6b7280' }}>Page {page} of {pages}</span>
            <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages} style={btnGhost}><ChevronRight size={14} /></button>
          </div>
        )}
      </div>

      {quickFill && <QuickFillModal order={quickFill} onClose={() => setQuickFill(null)} onSaved={() => { setQuickFill(null); loadOrders(); }} />}
      {showAdd && <OrderModal prefill={showAdd.prefill || null} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); loadOrders(); }} />}
      {editOrder && <OrderModal order={editOrder} onClose={() => setEditOrder(null)} onSaved={() => { setEditOrder(null); loadOrders(); }} />}
      {deleteOrder && <DeleteConfirm order={deleteOrder} onClose={() => setDeleteOrder(null)} onDeleted={() => { setDeleteOrder(null); loadOrders(); }} />}
    </div>
  );
}
